import { useState, useEffect, useCallback, useRef } from 'react';

export function useGeolocation({ 
  enableHighAccuracy = true,
  timeout = 10000,
  maximumAge = 0,
  watchPosition = false
}) {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const watchIdRef = useRef(null);

  const getLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported by your browser"));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          };
          resolve(loc);
        },
        (err) => {
          let message = "Unable to get location";
          switch (err.code) {
            case err.PERMISSION_DENIED:
              message = "Location permission denied. Please enable location access in your browser settings.";
              break;
            case err.POSITION_UNAVAILABLE:
              message = "Location unavailable. Please check your device's location services.";
              break;
            case err.TIMEOUT:
              message = "Location request timed out. Please try again.";
              break;
            default:
              message = "Unable to get location. Please try again.";
          }
          reject(new Error(message));
        },
        { enableHighAccuracy, timeout, maximumAge: 0 }
      );
    });
  }, [enableHighAccuracy, timeout]);

  const requestLocation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const loc = await getLocation();
      setLocation(loc);
      setError(null);
      return loc;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getLocation]);

  // Watch position with aggressive settings for real-time tracking
  useEffect(() => {
    if (!watchPosition || !navigator.geolocation) return;
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
        });
        setError(null);
      },
      () => {
        // Watch error — will keep retrying automatically
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,  // Always get fresh position, never use cache
      }
    );
    
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [watchPosition]);

  return {
    location,
    error,
    loading,
    requestLocation,
    setLocation,
    setError
  };
}
